// src/services/pms/apaleo-adapter.ts
import ApaleoAuthClient, {
  ALLOWED_PROPERTIES,
  setAllowedProperties,
  subscribeAllowedProperties,
  getAllowedPropertiesSnapshot,
} from '../apaleo';
import type { PMSAdapter } from './types';

/**
 * Loose view of the JS ApaleoAuth client. Every adapter method delegates via
 * `...args` spread, so the target only needs a permissive call signature —
 * the typed public surface is enforced on the adapter itself (see below).
 */
const ApaleoAuth = ApaleoAuthClient as unknown as Record<string, (...args: any[]) => any>;

/**
 * The Apaleo adapter surface: the full PMSAdapter contract plus the
 * apaleo-specific `refreshAccessToken` token-rotation method.
 */
export type ApaleoAdapter = PMSAdapter & {
  refreshAccessToken: (...args: unknown[]) => Promise<unknown>;
};

/**
 * Apaleo PMS adapter — wraps the existing ApaleoAuth client.
 *
 * Implements the PMSAdapter interface (see `./types.ts`).
 * Every method delegates to ApaleoAuth using `...args` spread so the
 * adapter introduces zero signature changes — callers keep working
 * exactly as before, just importing `pms` instead of `ApaleoAuth`.
 *
 * 61 externally-called methods are re-exported here, plus `rawFetch` — a
 * public escape hatch for authenticated raw-endpoint calls that have no
 * dedicated method yet.
 * 4 internal-only methods (_fetch, _splitPerNightGross, apiUrl, getUserInfo)
 * are intentionally NOT exposed under their internal names — they are
 * apaleo-internal.
 *
 * Method signatures are contextually typed from `PMSAdapter`: the `...args`
 * rest parameters receive the exact tuple types declared by the interface,
 * so the public surface is fully typed while delegation stays signature-free.
 */
export const apaleoAdapter: ApaleoAdapter = {
  // ── Auth ────────────────────────────────────────────────────────
  getToken: (...args) => ApaleoAuth.getToken(...args),
  setToken: (...args) => ApaleoAuth.setToken(...args),
  registerTokenGetter: (...args) => ApaleoAuth.registerTokenGetter(...args),
  refreshAccessToken: (...args) => ApaleoAuth.refreshAccessToken(...args),

  // ── Reservations ────────────────────────────────────────────────
  fetchReservations: (...args) => ApaleoAuth.fetchReservations(...args),
  fetchReservationsAllTime: (...args) => ApaleoAuth.fetchReservationsAllTime(...args),
  fetchReservationById: (...args) => ApaleoAuth.fetchReservationById(...args),
  updateReservation: (...args) => ApaleoAuth.updateReservation(...args),
  amendReservation: (...args) => ApaleoAuth.amendReservation(...args),
  checkInReservation: (...args) => ApaleoAuth.checkInReservation(...args),
  checkOutReservation: (...args) => ApaleoAuth.checkOutReservation(...args),
  cancelReservation: (...args) => ApaleoAuth.cancelReservation(...args),
  fetchReservationServices: (...args) => ApaleoAuth.fetchReservationServices(...args),
  fetchReservationBookedServices: (...args) => ApaleoAuth.fetchReservationBookedServices(...args),
  bookReservationService: (...args) => ApaleoAuth.bookReservationService(...args),
  deleteReservationService: (...args) => ApaleoAuth.deleteReservationService(...args),
  appendReservationComment: (...args) => ApaleoAuth.appendReservationComment(...args),
  autoAssignPreferredRoom: (...args) => ApaleoAuth.autoAssignPreferredRoom(...args),
  fetchReservationsByBlock: (...args) => ApaleoAuth.fetchReservationsByBlock(...args),
  fetchOpenFolioForReservation: (...args) => ApaleoAuth.fetchOpenFolioForReservation(...args),

  // ── Properties ──────────────────────────────────────────────────
  fetchProperties: (...args) => ApaleoAuth.fetchProperties(...args),
  fetchPropertyById: (...args) => ApaleoAuth.fetchPropertyById(...args),
  fetchPropertyPerformance: (...args) => ApaleoAuth.fetchPropertyPerformance(...args),
  fetchPropertyPaymentConfig: (...args) => ApaleoAuth.fetchPropertyPaymentConfig(...args),
  fetchUnitCounts: (...args) => ApaleoAuth.fetchUnitCounts(...args),
  fetchUnits: (...args) => ApaleoAuth.fetchUnits(...args),
  fetchUnitsByGroup: (...args) => ApaleoAuth.fetchUnitsByGroup(...args),
  fetchUnitGroups: (...args) => ApaleoAuth.fetchUnitGroups(...args),
  updateUnitConditions: (...args) => ApaleoAuth.updateUnitConditions(...args),
  setRoomStatus: (...args) => ApaleoAuth.setRoomStatus(...args),
  fetchAvailability: (...args) => ApaleoAuth.fetchAvailability(...args),

  // ── Bookings & Offers ───────────────────────────────────────────
  createBooking: (...args) => ApaleoAuth.createBooking(...args),
  fetchOffers: (...args) => ApaleoAuth.fetchOffers(...args),
  fetchRatePlans: (...args) => ApaleoAuth.fetchRatePlans(...args),
  fetchCancellationPolicy: (...args) => ApaleoAuth.fetchCancellationPolicy(...args),
  fetchServices: (...args) => ApaleoAuth.fetchServices(...args),

  // ── Invoices & Folios ───────────────────────────────────────────
  fetchInvoiceData: (...args) => ApaleoAuth.fetchInvoiceData(...args),
  fetchInvoicePdf: (...args) => ApaleoAuth.fetchInvoicePdf(...args),
  createInvoice: (...args) => ApaleoAuth.createInvoice(...args),
  cancelInvoice: (...args) => ApaleoAuth.cancelInvoice(...args),
  patchFolio: (...args) => ApaleoAuth.patchFolio(...args),
  addFolioCharge: (...args) => ApaleoAuth.addFolioCharge(...args),
  postAllowanceForCharge: (...args) => ApaleoAuth.postAllowanceForCharge(...args),
  updateFolioCompany: (...args) => ApaleoAuth.updateFolioCompany(...args),
  fetchFoliosForReservation: (...args) => ApaleoAuth.fetchFoliosForReservation(...args),
  addFolioPayment: (...args) => ApaleoAuth.addFolioPayment(...args),
  fetchPaymentAccountsForReservation: (...args) => ApaleoAuth.fetchPaymentAccountsForReservation(...args),
  addFolioPaymentByPaymentAccount: (...args) => ApaleoAuth.addFolioPaymentByPaymentAccount(...args),
  closeFolio: (...args) => ApaleoAuth.closeFolio(...args),
  reopenFolio: (...args) => ApaleoAuth.reopenFolio(...args),
  createFolio: (...args) => ApaleoAuth.createFolio(...args),
  moveCharges: (...args) => ApaleoAuth.moveCharges(...args),
  moveAllCharges: (...args) => ApaleoAuth.moveAllCharges(...args),
  fetchAccountsSchema: (...args) => ApaleoAuth.fetchAccountsSchema(...args),
  fetchTransactionsExport: (...args) => ApaleoAuth.fetchTransactionsExport(...args),
  fetchSettlements: (...args) => ApaleoAuth.fetchSettlements(...args),
  fetchSettlementCsv: (...args) => ApaleoAuth.fetchSettlementCsv(...args),
  fetchSettlementXlsx: (...args) => ApaleoAuth.fetchSettlementXlsx(...args),

  // ── City Tax ────────────────────────────────────────────────────
  fetchCityTaxAuditData: (...args) => ApaleoAuth.fetchCityTaxAuditData(...args),
  recalculateCityTaxes: (...args) => ApaleoAuth.recalculateCityTaxes(...args),
  splitHrsClubRates: (...args) => ApaleoAuth.splitHrsClubRates(...args),
  removeReservationCityTax: (...args) => ApaleoAuth.removeReservationCityTax(...args),
  cleanupBookingNames: (...args) => ApaleoAuth.cleanupBookingNames(...args),

  // ── Maintenance ─────────────────────────────────────────────────
  fetchMaintenances: (...args) => ApaleoAuth.fetchMaintenances(...args),
  createMaintenance: (...args) => ApaleoAuth.createMaintenance(...args),
  updateMaintenance: (...args) => ApaleoAuth.updateMaintenance(...args),
  deleteMaintenance: (...args) => ApaleoAuth.deleteMaintenance(...args),

  // ── Groups & Blocks ─────────────────────────────────────────────
  fetchGroups: (...args) => ApaleoAuth.fetchGroups(...args),
  createGroup: (...args) => ApaleoAuth.createGroup(...args),
  fetchBlocks: (...args) => ApaleoAuth.fetchBlocks(...args),
  createBlock: (...args) => ApaleoAuth.createBlock(...args),
  updateBlock: (...args) => ApaleoAuth.updateBlock(...args),
  addReservationToGroup: (...args) => ApaleoAuth.addReservationToGroup(...args),
  fetchRoutings: (...args) => ApaleoAuth.fetchRoutings(...args),
  createRouting: (...args) => ApaleoAuth.createRouting(...args),
  deleteRouting: (...args) => ApaleoAuth.deleteRouting(...args),

  // ── Companies ───────────────────────────────────────────────────
  fetchCompanies: (...args) => ApaleoAuth.fetchCompanies(...args),
  fetchCompanyInvoicesReport: (...args) => ApaleoAuth.fetchCompanyInvoicesReport(...args),

  // ── Webhooks ───────────────────────────────────────────────────
  registerApaleoWebhook: (...args) => ApaleoAuth.registerApaleoWebhook(...args),

  // ── Raw endpoint access (escape hatch) ─────────────────────────
  rawFetch: (...args) => ApaleoAuth._fetch(...args),

  // ── ALLOWED_PROPERTIES setter (required by PMSAdapter) ──────────
  // Delegates to the live setter imported from apaleo.js — the named
  // re-export below stays the canonical import path for consumers.
  setAllowedProperties: (...args) => setAllowedProperties(...args),

  // ── ALLOWED_PROPERTIES (mutable — getter keeps the live binding) ──
  get ALLOWED_PROPERTIES() { return ALLOWED_PROPERTIES; },
};

// Re-export ALLOWED_PROPERTIES and setAllowedProperties as live bindings.
// `ALLOWED_PROPERTIES` is a mutable `let` in apaleo.js — destructuring or
// object-property assignment would freeze it at the initial empty array.
// The named re-export below preserves the live binding so consumers see
// updates after `setAllowedProperties()` runs.
// subscribeAllowedProperties/getAllowedPropertiesSnapshot back the React
// `useAllowedProperties()` hook — see apaleo.ts for the rationale.
export {
  ALLOWED_PROPERTIES,
  setAllowedProperties,
  subscribeAllowedProperties,
  getAllowedPropertiesSnapshot,
};

export default apaleoAdapter;
